import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, Ear, Footprints, PersonStanding, AlertCircle, Quote, X } from "lucide-react";
import { api, getSavedProfile } from "../lib/api";
import { toast } from "sonner";
import { PageShell } from "../components/PageShell";
import { AppText, Button, Card, Medal, Pastille, Sticker, Section } from "../components/ui";
import { Seo } from "../components/Seo";

const DISCOVER = [
  { icon: Footprints, title: "Ton profil moteur", desc: "Terrien ou aérien : ta façon naturelle de poser le pied." },
  { icon: PersonStanding, title: "Ton score de stabilité", desc: "Est-ce que ton bassin tient quand tu cours ?" },
  { icon: Footprints, title: "Le verdict sur tes chaussures", desc: "Cohérentes avec ta foulée, ou un frein ?" },
  { icon: AlertCircle, title: "Les 3 erreurs que tu fais probablement", desc: "Celles que presque tous les débutants font." },
];
const WHY = [
  "Les plans sont identiques pour tout le monde.",
  "Personne ne regarde tes chaussures.",
  "On te fait courir à une allure au lieu de t'apprendre à t'écouter.",
];
const METHOD = [
  { icon: Ear, text: "Dès que ta foulée fait du bruit, tu marches." },
  { icon: PersonStanding, text: "La montre se regarde après, jamais pendant." },
  { icon: Footprints, text: "Marcher n'est pas un échec. C'est la méthode." },
];
const NOT_HERE = ["+10 % par semaine", "la planche au sol", "les étirements comme solution", "la zone 2 exclusive", "l'esclavage de la montre"];
const GALLERY = [
  { key: "01", when: "Ta 1re séance" },
  { key: "03", when: "3 semaines" },
  { key: "05", when: "20-30 min sans t'arrêter" },
  { key: "10", when: "12 semaines" },
  { key: "21", when: "Le semi" },
  { key: "42", when: "Le marathon" },
];
const REASSURE = ["Programme réalisé par IA", "Supervisé par un entraîneur confirmé", "25 000 analyses de foulée", "Moins de 5 € par mois"];

export default function Home() {
  const navigate = useNavigate();
  const testimonials = useQuery({ queryKey: ["testimonials"], queryFn: api.testimonials, staleTime: 600000, retry: false });
  const start = () => navigate("/profil");
  const choose = async (plan) => {
    const ref = localStorage.getItem("km1_ref") || undefined;
    const pid = getSavedProfile();
    try {
      const res = pid
        ? await api.checkout(pid, plan, undefined, ref)
        : await api.checkoutDiscovery(plan, { anonId: localStorage.getItem("km1_anon_id") || undefined, referralCode: ref });
      if (res.url) { window.location.assign(res.url); return; }
      navigate(pid ? `/programme?profile=${pid}&plan=${plan}` : "/profil");
    } catch (e) { toast.error(e.message || "Paiement indisponible, réessaie"); }
  };
  const lastProfile = typeof window !== "undefined" ? localStorage.getItem("km1_last_profile") : null;

  return (
    <PageShell>
      <Seo title="Ton KM01 commence ici" description="Le premier kilomètre est le plus important. Programme de course à pied pour débutants construit à partir de ta façon de courir. Profil de coureur gratuit en 5 minutes : terrien ou aérien, stabilité, chaussures, erreurs." />

      {/* Hero */}
      <div className="relative pt-6 pb-6 flex flex-col items-center overflow-hidden" data-testid="hero">
        <div className="absolute -top-10 -right-20 w-64 h-64 rounded-full bg-km-coralt opacity-80" />
        <div className="absolute top-28 -left-24 w-56 h-56 rounded-full bg-km-sunny opacity-25" />
        <div className="relative flex flex-col items-center px-6">
          <div className="relative">
            <Medal size={190} testID="hero-medal" />
            <Sticker text="Gratuit · 5 min" tilt={-8} className="absolute right-0 top-6" testID="hero-sticker" />
          </div>
        </div>
        <h1 className="relative mt-4 font-display font-extrabold text-center text-[40px] leading-[46px] text-km-ink">
          Ton <span className="text-km-coral">KM01</span> commence ici.
        </h1>
        <p className="relative mt-3 font-text text-[18px] leading-[27px] text-center text-km-ontertiary">
          Le premier kilomètre est le plus important. Un programme de course pour débutants, construit à partir de <strong>TA</strong> façon de courir. Pas d'un tableau générique.
        </p>
        <Button title="Découvre ton profil de coureur" onClick={start} testID="hero-cta"
          className="mt-6 w-full" icon={<ArrowRight size={20} strokeWidth={2.5} />} />
        <AppText variant="caption" className="mt-2 text-km-muted text-center">Gratuit · 5 minutes · sans compte, sans carte bancaire.</AppText>
        {lastProfile ? (
          <button data-testid="resume-order" onClick={() => navigate(`/resultat/${lastProfile}`)}
            className="mt-4 w-full flex items-center justify-between gap-3 rounded-km bg-km-sage/15 border border-km-sage/40 px-4 py-3 text-left transition-colors hover:bg-km-sage/25">
            <div>
              <AppText variant="bodyStrong">Reprendre ma commande</AppText>
              <AppText variant="caption" className="text-km-muted">Ton profil est prêt — reviens à ton bilan pour démarrer ton programme.</AppText>
            </div>
            <ArrowRight size={20} strokeWidth={2.5} className="text-km-sage shrink-0" />
          </button>
        ) : null}
      </div>

      {/* Histoire 0 → 1 */}
      <div className="flex items-center gap-4 bg-white rounded-km p-4 border border-km-border mt-4 shadow-soft" data-testid="story-01">
        <div className="flex items-center gap-1">
          <span className="font-display font-extrabold text-[44px] leading-[48px] text-km-coral">0</span>
          <ArrowRight size={18} strokeWidth={2.5} className="text-km-muted" />
          <span className="font-display font-extrabold text-[44px] leading-[48px] text-km-ink">1</span>
        </div>
        <div className="flex-1">
          <AppText variant="bodyStrong">Le 0, c'est toi avant. Le 1, c'est ton premier kilomètre.</AppText>
          <AppText variant="caption" className="text-km-ontertiary mt-1">Entre les deux, il n'y a qu'un pas. Tout le monde a un KM01. Ensuite viennent le KM03, le KM05, le KM10 — à ton rythme, sans montre.</AppText>
        </div>
      </div>

      {/* Réassurance */}
      <div className="flex flex-wrap gap-2 mt-4 justify-center" data-testid="reassurance">
        {REASSURE.map((t) => (
          <div key={t} className="flex items-center gap-1.5 bg-white rounded-full px-3 py-2 border border-km-border">
            <CheckCircle2 size={16} className="text-km-sage" fill="currentColor" stroke="#fff" />
            <AppText variant="caption" className="font-bold">{t}</AppText>
          </div>
        ))}
      </div>

      {/* Découvrir */}
      <Section title="Ce que tu vas découvrir en 5 minutes">
        <div className="flex flex-col gap-3">
          {DISCOVER.map(({ icon: Icon, title, desc }) => (
            <Card key={title} className="flex items-center gap-4 p-4">
              <div className="w-13 h-13 rounded-2xl bg-km-coralt flex items-center justify-center shrink-0" style={{ transform: "rotate(-6deg)", width: 52, height: 52 }}>
                <Icon size={26} className="text-km-coral" />
              </div>
              <div className="flex-1">
                <AppText variant="bodyStrong">{title}</AppText>
                <AppText variant="caption" className="text-km-muted mt-0.5">{desc}</AppText>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* Médailles */}
      <Section title="Six médailles à gagner">
        <Card className="flex flex-col gap-4 items-center" testID="medal-gallery">
          <AppText variant="body" className="text-km-ontertiary text-center">Chaque palier est un kilomètre symbolique. Tu les débloques une à une, à ton rythme, sans montre.</AppText>
          <div className="flex flex-wrap justify-center gap-2">
            {GALLERY.map((m) => (
              <div key={m.key} className="w-[30%] min-w-[96px] flex flex-col items-center gap-0.5 py-2 rounded-2xl bg-km-cream" data-testid={`gallery-${m.key}`}>
                <Medal value={m.key} size={84} />
                <AppText variant="bodyStrong" className="text-[15px]">KM{m.key}</AppText>
                <AppText variant="caption" className="text-km-muted text-center px-1">{m.when}</AppText>
              </div>
            ))}
          </div>
          <Sticker text="La première se gagne dès ta 1re séance" tone="brand" tilt={-2} />
        </Card>
      </Section>

      {/* Témoignages */}
      {testimonials.data && testimonials.data.length > 0 ? (
        <Section title="Ils ont passé leur KM05">
          <div className="flex flex-col gap-3" data-testid="testimonials">
            {testimonials.data.map((t, i) => (
              <div key={t.id} className="relative bg-white rounded-km p-4 border border-km-border shadow-soft flex flex-col gap-3" style={{ transform: `rotate(${i % 2 ? 0.6 : -0.6}deg)` }} data-testid={`testimonial-${t.id}`}>
                <Quote size={28} className="absolute top-3 left-3.5 text-km-coral opacity-35" fill="currentColor" />
                <AppText variant="body" className="text-[17px] leading-[26px] pl-4">{t.text}</AppText>
                <div className="flex items-center gap-2 pl-4">
                  <Medal value={t.milestone} size={32} />
                  <AppText variant="caption" className="text-km-muted font-bold">{t.first_name} · a passé son {t.milestone}</AppText>
                </div>
              </div>
            ))}
            <AppText variant="caption" className="text-km-muted text-center">Des mots de vrais coureurs KM01, relus par Éric. Aucun avis inventé.</AppText>
          </div>
        </Section>
      ) : null}

      {/* Pourquoi */}
      <Section title="Pourquoi 1 débutant sur 2 abandonne">
        <div className="flex flex-col gap-3">
          {WHY.map((t, i) => (
            <div key={t} className="flex items-center gap-4">
              <Pastille value={i + 1} size={32} tone="ink" />
              <AppText variant="body" className="flex-1 text-[18px] leading-[26px]">{t}</AppText>
            </div>
          ))}
        </div>
      </Section>

      {/* Méthode en 3 phrases */}
      <Section title="La méthode en trois phrases">
        <div className="flex flex-col gap-4">
          {METHOD.map(({ icon: Icon, text }, i) => (
            <div key={text} className="bg-white rounded-km p-6 flex flex-col gap-4 border border-km-border shadow-soft" data-testid="method-card">
              <div className="w-22 h-22 rounded-3xl bg-km-coral flex items-center justify-center shadow-coral" style={{ width: 88, height: 88, transform: `rotate(${i % 2 ? 6 : -6}deg)` }}>
                <Icon size={52} className="text-white" />
              </div>
              <AppText variant="title" className="text-[24px] leading-[30px]">« {text} »</AppText>
            </div>
          ))}
        </div>
      </Section>

      {/* Ce que tu ne trouveras pas */}
      <Section title="Ce que tu ne trouveras pas ici">
        <Card className="flex flex-col gap-3">
          {NOT_HERE.map((t) => (
            <div key={t} className="flex items-center gap-3">
              <X size={22} strokeWidth={2.5} className="text-km-coral" />
              <AppText variant="body">{t}</AppText>
            </div>
          ))}
        </Card>
      </Section>

      {/* Coach */}
      <Section title="Le coach derrière l'IA">
        <Card className="flex flex-col items-center" testID="coach-card">
          <img src="/assets/coach/eric.jpg" alt="Éric, coach KM01" className="w-28 h-28 rounded-full object-cover border-4 border-km-coral" />
          <AppText variant="subtitle" className="mt-3">Éric, notre coach</AppText>
          <AppText variant="body" className="text-km-ontertiary text-center mt-2">Entraîneur depuis 1995, expert running chez Top Performance depuis 1998. 25 000 analyses de foulée.</AppText>
          <AppText variant="body" className="text-km-ontertiary text-center mt-2">Auteur de « Libère ta foulée », le livre qui explique la méthode et les besoins de chaque coureur.</AppText>
          <AppText variant="body" className="text-center mt-3 italic">« Je ne suis pas là pour te commander : je marche à côté de toi. Chaque programme suit ma méthode et je relis personnellement les cas qui le nécessitent. »</AppText>
        </Card>
      </Section>

      {/* Tarifs */}
      <Section title="Combien ça coûte ?">
        <div className="flex flex-col gap-3" data-testid="pricing-card">
          <Card className="flex items-center gap-3 p-4">
            <div className="flex-1">
              <AppText variant="bodyStrong">Ton profil de coureur</AppText>
              <AppText variant="caption" className="text-km-muted">5 minutes, sans compte</AppText>
            </div>
            <Sticker text="Gratuit" tone="sage" tilt={4} />
          </Card>
          <div className="flex gap-3">
            <button onClick={() => choose("3m")} data-testid="home-tarif-3m" className="flex-1 text-left">
              <Card className="relative h-full flex flex-col items-center gap-1 p-4 pt-6 border-2 border-km-coral cursor-pointer transition-transform hover:-translate-y-0.5">
                <Sticker text="Le plus choisi" tilt={-4} className="absolute -top-3 left-3" />
                <Medal value="10" size={64} />
                <AppText variant="label" className="text-km-muted">Programme 10K · 3 mois</AppText>
                <AppText variant="title" className="text-km-coral">14,90 €</AppText>
                <AppText variant="caption" className="text-km-muted text-center">courir 5 ou 10 km</AppText>
                <span className="mt-2 px-4 py-2 rounded-full bg-km-coral text-white font-text font-bold text-[14px]">Je vise le 10K</span>
              </Card>
            </button>
            <button onClick={() => choose("6m")} data-testid="home-tarif-6m" className="flex-1 text-left">
              <Card className="h-full flex flex-col items-center gap-1 p-4 pt-6 cursor-pointer transition-transform hover:-translate-y-0.5">
                <Medal value="21" size={64} />
                <AppText variant="label" className="text-km-muted">Programme 21/42K · 6 mois</AppText>
                <AppText variant="title" className="text-km-coral">24,90 €</AppText>
                <AppText variant="caption" className="text-km-muted text-center">premier semi ou marathon</AppText>
                <span className="mt-2 px-4 py-2 rounded-full bg-white border-2 border-km-coral text-km-coral font-text font-bold text-[14px]">Je vise le 21/42K</span>
              </Card>
            </button>
          </div>
          <AppText variant="caption" className="text-km-muted text-center" data-testid="home-trial-note">Et pour commencer : <strong>1 mois d'essai gratuit</strong> après ton profil, sans carte, non renouvelable.</AppText>
          <Button title="Commencer par mon profil — gratuit" onClick={start} testID="pricing-cta" />
        </div>
      </Section>
    </PageShell>
  );
}
